// ###### IMPORTS
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { TableCellsIcon, ClipboardDocumentIcon } from '@heroicons/react/24/solid';
import { getAssetsYearReport } from '../../viewServices/assets_api';
import DropdownYearGridDark from 'views/finance/moduleComponents/DropdownYearGridDark';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const REPORT_COLUMNS = [
  'category_l1',
  'category_l2',
  'asset_desc',
  'asset_id',
  'qty_year1',
  'value_year1',
  'qty_year2',
  'value_year2',
  'flag',
];

const QTY_COLUMNS = new Set(['qty_year1', 'qty_year2']);
const VALUE_COLUMNS = new Set(['value_year1', 'value_year2']);
const RIGHT_ALIGN_COLUMNS = new Set([...QTY_COLUMNS, ...VALUE_COLUMNS]);

const formatColumnHeader = (key, year1, year2) =>
  key.replace('year1', String(year1)).replace('year2', String(year2));

const formatCellValue = (key, value) => {
  if (QTY_COLUMNS.has(key)) {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  }
  if (VALUE_COLUMNS.has(key)) {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!Number.isNaN(n) && typeof value === 'number') {
      return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return String(value);
  }
  return value ?? '';
};

const buildClipboardText = (rows, year1, year2) => {
  const header = REPORT_COLUMNS.map((key) => formatColumnHeader(key, year1, year2)).join('\t');
  const body = (rows || [])
    .map((row) => REPORT_COLUMNS.map((key) => formatCellValue(key, row[key])).join('\t'))
    .join('\n');
  return `${header}\n${body}`;
};

const formatApiError = (data) => {
  if (!data) return 'Request failed.';
  if (typeof data === 'string') return data;
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  return 'Request failed.';
};

const defaultYearsFromRange = (yearsRange) => {
  const opts = yearsRange?.length ? yearsRange.map(String) : [String(new Date().getFullYear())];
  const year2 = opts[opts.length - 1];
  const year1 = opts.length >= 2 ? opts[opts.length - 2] : String(parseInt(year2, 10) - 1);
  return { year1, year2, opts };
};

const VALUE_MODE_OPTIONS = ['net', 'total'];

// ##### MAIN COMPONENT
const CardYearReport = ({ yearsRange = [] }) => {
  const year1Ref = useRef(null);
  const year2Ref = useRef(null);
  const valueModeRef = useRef(null);

  const yearOptions = useMemo(
    () => (yearsRange?.length ? yearsRange.map(String) : [String(new Date().getFullYear())]),
    [yearsRange]
  );

  const [year1, setYear1] = useState(() => defaultYearsFromRange(yearsRange).year1);
  const [year2, setYear2] = useState(() => defaultYearsFromRange(yearsRange).year2);
  const [valueMode, setValueMode] = useState('net');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!yearsRange?.length) return;
    const { year1: y1, year2: y2 } = defaultYearsFromRange(yearsRange);
    setYear1(y1);
    setYear2(y2);
  }, [yearsRange]);

  const handleGenerate = async () => {
    const y1 = parseInt(year1, 10);
    const y2 = parseInt(year2, 10);
    if (!Number.isFinite(y1) || !Number.isFinite(y2)) {
      toast.error('Select valid years.');
      return;
    }
    if (y1 >= y2) {
      toast.error('Year 1 must be before Year 2.');
      return;
    }

    setLoading(true);
    try {
      const res = await getAssetsYearReport(y1, y2, valueMode);
      setReport(res.data || null);
      if (!(res.data?.rows || []).length) {
        toast.success('Report generated with no rows (no positions in either year).');
      } else {
        toast.success(`Report generated: ${res.data.rows.length} row(s).`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data) || err.message || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!report?.rows?.length) {
      toast.error('Generate a report with rows before copying.');
      return;
    }
    const text = buildClipboardText(report.rows, report.year1, report.year2);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Report copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const year1Invalid = parseInt(year1, 10) >= parseInt(year2, 10);

  return (
    <div className="h-auto bg-white shadow rounded-xl p-4 flex flex-col gap-4">
      <div className="bg-green-800 border-b border-gray-200 -m-4 mb-4 rounded-t-xl">
        <div className="px-3 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <TableCellsIcon className="h-4 w-4 text-white" />
            <h2 className="text-sm font-medium text-white">Year Report</h2>
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600">
        Compare asset positions between two year-ends (31/Dec) using operation cost basis. Tradeable
        assets show quantity and value; deposits show value only.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-2 rounded-xl bg-gray-600 px-2 py-1.5 shadow-sm">
          <div ref={year1Ref} data-portal="year-report-y1">
            <DropdownYearGridDark
              options={yearOptions}
              selectedValue={year1}
              onSelect={setYear1}
              placeholder="Year"
              disabled={loading}
              usePortal={true}
              portalRef={year1Ref}
            />
          </div>
          <span className="text-sm text-white/70 px-0.5" aria-hidden>
            →
          </span>
          <div ref={year2Ref} data-portal="year-report-y2">
            <DropdownYearGridDark
              options={yearOptions}
              selectedValue={year2}
              onSelect={setYear2}
              placeholder="Year"
              disabled={loading}
              usePortal={true}
              portalRef={year2Ref}
            />
          </div>
        </div>
        <div className="inline-flex rounded-xl bg-gray-600 px-2 py-1.5 shadow-sm" ref={valueModeRef} data-portal="year-report-value-mode">
          <DropdownYearGridDark
            options={VALUE_MODE_OPTIONS}
            selectedValue={valueMode}
            onSelect={setValueMode}
            placeholder="Value"
            disabled={loading}
            usePortal={true}
            portalRef={valueModeRef}
            gridCols="grid-cols-2"
            width="w-24"
          />
        </div>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={loading || year1Invalid}
          className={`px-6 py-3 rounded-xl shadow-md text-white ${
            loading || year1Invalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'
          }`}
          title={year1Invalid ? 'Year 1 must be before Year 2' : 'Generate year-over-year report'}
        >
          {loading ? 'Generating…' : 'Generate'}
        </button>
        {report?.rows?.length > 0 && (
          <button
            type="button"
            onClick={handleCopy}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm"
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy to clipboard
          </button>
        )}
      </div>

      {year1Invalid && (
        <p className="text-xs text-amber-700">Year 1 must be strictly before Year 2.</p>
      )}

      {loading && (
        <div className="flex justify-center py-6">
          <SpinnerLoad />
        </div>
      )}

      {!loading && report?.rows?.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-xs text-left">
            <thead className="bg-gray-50 text-gray-600 uppercase tracking-wide">
              <tr>
                {REPORT_COLUMNS.map((key) => (
                  <th
                    key={key}
                    className={`px-2 py-2 whitespace-nowrap ${RIGHT_ALIGN_COLUMNS.has(key) ? 'text-right' : ''}`}
                  >
                    {formatColumnHeader(key, report.year1, report.year2)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {report.rows.map((row) => (
                <tr key={row.asset_id} className="hover:bg-gray-50/80">
                  {REPORT_COLUMNS.map((key) => (
                    <td
                      key={key}
                      className={`px-2 py-1.5 text-gray-800 ${
                        RIGHT_ALIGN_COLUMNS.has(key) ? 'text-right tabular-nums' : ''
                      } ${key === 'asset_id' ? 'font-mono text-gray-700' : ''} ${
                        key === 'asset_desc' ? 'max-w-[12rem] truncate' : ''
                      }`}
                      title={key === 'asset_desc' ? row.asset_desc : undefined}
                    >
                      {formatCellValue(key, row[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && report && report.rows?.length === 0 && (
        <p className="text-sm text-gray-500 py-2">No assets with position in either selected year.</p>
      )}
    </div>
  );
};

export default CardYearReport;
